import { Box, Button, Group, Paper, Text } from "@mantine/core";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FLEX_DEMO_STEPS } from "./flexDemo.const";
import {
  getFlexDemoCoachCopy,
  flexDemoStepIndex,
  resolveExpressInterestCoachFromDom,
  resolvePayCoachFromDom,
  type FlexDemoCoachCopy,
} from "./flexDemo.coach";
import { useFlexDemoSessionOptional } from "./FlexDemoSessionContext";

const MOBILE_MAX_W = 768;
const GAP = 16;
const PAD = 8;
const DESKTOP_CARD_W = 340;
const DESKTOP_DOCK_W = 360;
const ESTIMATED_CARD_H = 200;
const HEADER_OFFSET = 72;

function queryTarget(id: string): Element | null {
  return document.querySelector(`[data-flex-demo-tour="${id}"]`);
}

function readRect(el: Element | null): DOMRect | null {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < 4 || rect.height < 4) return null;
  return rect;
}

type CardPos = {
  top?: number;
  left: number;
  bottom?: number;
  width: number;
};

function overlaps(
  a: { top: number; left: number; width: number; height: number },
  b: { top: number; left: number; width: number; height: number },
): boolean {
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

function placeDesktopBesideHole(
  hole: { top: number; left: number; width: number; height: number },
  cardW: number,
): CardPos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rightEdge = hole.left + hole.width;
  const candidates: CardPos[] = [
    {
      top: Math.min(Math.max(HEADER_OFFSET, hole.top), vh - ESTIMATED_CARD_H),
      left: rightEdge + GAP,
      width: cardW,
    },
    {
      top: Math.min(Math.max(HEADER_OFFSET, hole.top), vh - ESTIMATED_CARD_H),
      left: hole.left - GAP - cardW,
      width: cardW,
    },
    {
      top: hole.top + hole.height + GAP,
      left: Math.min(Math.max(GAP, hole.left), vw - cardW - GAP),
      width: cardW,
    },
    {
      top: Math.max(HEADER_OFFSET, hole.top - GAP - ESTIMATED_CARD_H),
      left: Math.min(Math.max(GAP, hole.left), vw - cardW - GAP),
      width: cardW,
    },
    // Fallback: top-right chrome — never sit on the hole / modal body.
    {
      top: HEADER_OFFSET,
      left: Math.max(GAP, vw - cardW - GAP),
      width: cardW,
    },
  ];

  for (const pos of candidates) {
    if (pos.left < GAP || pos.left + pos.width > vw - GAP) continue;
    if ((pos.top ?? 0) < HEADER_OFFSET - 4) continue;
    if ((pos.top ?? 0) + ESTIMATED_CARD_H > vh - GAP) continue;
    const cardBox = {
      top: pos.top ?? 0,
      left: pos.left,
      width: pos.width,
      height: ESTIMATED_CARD_H,
    };
    if (!overlaps(cardBox, hole)) return pos;
  }
  return candidates[candidates.length - 1];
}

/**
 * Keep the coach off centered modal content.
 * - Mobile: always compact bottom sheet (top dock hid too much of the form)
 * - Desktop preferTop (Express Interest / pay): top-right corner
 * - Desktop default: bottom-right corner
 */
function placeDock(isMobile: boolean, preferTop: boolean): CardPos {
  const vw = window.innerWidth;
  if (isMobile) {
    return { bottom: 0, left: 0, width: vw };
  }
  const width = Math.min(DESKTOP_DOCK_W, vw - GAP * 2);
  const left = Math.max(GAP, vw - width - GAP);
  if (preferTop) {
    return { top: HEADER_OFFSET, left, width };
  }
  return { bottom: GAP, left, width };
}

export default function FlexDemoCoach() {
  const ctx = useFlexDemoSessionOptional();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_W,
  );
  const scrolledForSpotlightRef = useRef<string | null>(null);

  const step = ctx?.session.step;
  const [liveCopy, setLiveCopy] = useState<FlexDemoCoachCopy | null>(null);

  const baseCopy = step
    ? step === "pay"
      ? null
      : getFlexDemoCoachCopy(
          step,
          Boolean(ctx?.session.collaborationsNavOpened),
          Boolean(ctx?.session.workspaceOpened),
        )
    : null;

  const copy =
    step === "express-interest" || step === "pay" ? liveCopy : baseCopy;

  const refreshCopyAndRect = useCallback(() => {
    setIsMobile(window.innerWidth < MOBILE_MAX_W);
    const nextCopy =
      step === "express-interest"
        ? resolveExpressInterestCoachFromDom()
        : step === "pay"
          ? resolvePayCoachFromDom()
          : step
            ? getFlexDemoCoachCopy(
                step,
                Boolean(ctx?.session.collaborationsNavOpened),
                Boolean(ctx?.session.workspaceOpened),
              )
            : null;
    if (step === "express-interest" || step === "pay") setLiveCopy(nextCopy);

    const spotlight = nextCopy?.spotlight ?? null;
    if (!spotlight) {
      setRect(null);
      scrolledForSpotlightRef.current = null;
      return;
    }
    const el = queryTarget(spotlight);
    if (el && scrolledForSpotlightRef.current !== spotlight) {
      const next = el.getBoundingClientRect();
      // Leave room for the mobile bottom coach sheet so CTAs stay visible above it.
      const sheetReserve = window.innerWidth < MOBILE_MAX_W ? 168 : 72;
      if (next.top < 72 || next.bottom > window.innerHeight - sheetReserve) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      scrolledForSpotlightRef.current = spotlight;
      window.setTimeout(() => setRect(readRect(queryTarget(spotlight))), 280);
    }
    setRect(readRect(el));
  }, [ctx?.session.collaborationsNavOpened, ctx?.session.workspaceOpened, step]);

  useLayoutEffect(() => {
    scrolledForSpotlightRef.current = null;
    refreshCopyAndRect();
  }, [refreshCopyAndRect, step]);

  useEffect(() => {
    const onWin = () => refreshCopyAndRect();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const t = window.setInterval(refreshCopyAndRect, 300);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.clearInterval(t);
    };
  }, [refreshCopyAndRect]);

  if (!ctx || !copy) return null;
  // Finish screen owns its own in-flow coach UI — floating card breaks that layout.
  if (ctx.session.step === "finish") return null;

  const stepIndex = flexDemoStepIndex(ctx.session.step);
  const targetReady = Boolean(rect && rect.width > 0);
  const effectiveBlocking = copy.blocking !== false && targetReady;
  const hole =
    effectiveBlocking && rect
      ? {
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }
      : null;

  // Desktop modal steps: top-right. Mobile: always bottom (top dock covered the form).
  const preferTopDesktop = Boolean(copy.preferTopDock) && !isMobile;
  const docked = preferTopDesktop || isMobile || !hole;
  const cardPos: CardPos = docked
    ? placeDock(isMobile, preferTopDesktop)
    : placeDesktopBesideHole(hole!, DESKTOP_CARD_W);
  const mobileSheet = isMobile && docked;
  const compact = docked;

  const cancelControl = (
    <Button
      variant="subtle"
      color="dark"
      size="compact-xs"
      loading={ctx.leavingDemo}
      onClick={() => void ctx.cancelDemo()}
      px={4}
    >
      Cancel demo
    </Button>
  );

  const coachBody = (
    <>
      <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap" mb={4}>
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">
          Demo · Step {stepIndex + 1} of {FLEX_DEMO_STEPS.length}
        </Text>
        {mobileSheet ? cancelControl : null}
      </Group>
      <Text fw={800} size={mobileSheet ? "sm" : compact ? "sm" : "md"} mb={mobileSheet ? 4 : 4}>
        {copy.pageName}
      </Text>
      {!mobileSheet ? (
        <Text size="xs" c="dimmed" mb={6} style={{ lineHeight: 1.4 }}>
          {copy.pageIsFor}
        </Text>
      ) : null}
      <Text size="sm" fw={700} c="primary.7" mb={mobileSheet ? 0 : 6}>
        Next: {copy.nextClick}
      </Text>
      {!mobileSheet ? <Group justify="flex-end">{cancelControl}</Group> : null}
    </>
  );

  const coachPaper = (
    <Paper
      shadow="md"
      p={mobileSheet ? "sm" : compact ? "sm" : "md"}
      radius={mobileSheet ? "md md 0 0" : "md"}
      pos="fixed"
      data-flex-demo-coach
      style={{
        top: cardPos.top,
        left: cardPos.left,
        bottom: cardPos.bottom,
        width: cardPos.width,
        maxWidth: cardPos.width,
        boxSizing: "border-box",
        // Keep mobile coach short so the modal/form stays readable above it.
        maxHeight: mobileSheet ? 148 : compact ? 220 : undefined,
        overflow: docked ? "auto" : undefined,
        zIndex: effectiveBlocking ? 10052 : 10060,
        pointerEvents: "auto",
        border: "1px solid rgba(148, 163, 184, 0.35)",
        borderBottom: mobileSheet ? "none" : undefined,
        paddingBottom: mobileSheet ? "max(10px, env(safe-area-inset-bottom))" : undefined,
      }}
    >
      {coachBody}
    </Paper>
  );

  if (!effectiveBlocking) {
    return coachPaper;
  }

  return (
    <Box pos="fixed" inset={0} style={{ zIndex: 10040, pointerEvents: "none" }} data-flex-demo-coach-root>
      {hole ? (
        <>
          <Box
            pos="fixed"
            top={0}
            left={0}
            right={0}
            h={Math.max(0, hole.top)}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={0}
            right={0}
            top={hole.top + hole.height}
            bottom={0}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={0}
            w={Math.max(0, hole.left)}
            top={hole.top}
            h={hole.height}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={hole.left + hole.width}
            right={0}
            top={hole.top}
            h={hole.height}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              borderRadius: 10,
              boxShadow: "0 0 0 3px #ffc300",
              pointerEvents: "none",
            }}
          />
        </>
      ) : (
        <Box pos="fixed" inset={0} bg="rgba(15, 23, 42, 0.25)" style={{ pointerEvents: "none" }} />
      )}
      {coachPaper}
    </Box>
  );
}
